import React, { useState, useEffect, useContext } from "react";
import { Box, Card, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import Icons from "../Icons";
import { getSfaData } from "../../utils/apis";
import { AuthContext } from "../../context/AuthContext";
import MonthlyFilter from "./MonthlyFilter";
import AlertImg from "../../assests/images/alertimg.svg";
import StarBig from "../../assests/images/starbig.svg";
import { KEY_AUTH } from "../../utils/contants";
import { relative } from "path";

type TargetProgressCardProps = { contactId?: string | null };

const TargetProgressCard: React.FC<TargetProgressCardProps> = ({ contactId }) => {
  const authContext = useContext(AuthContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const now = new Date();
  const fallbackMonthLong = now.toLocaleString("en-US", { month: "long" });
  const fallbackYear = now.getFullYear();

  const [currentSticks, setCurrentSticks] = useState(0);
  const [totalSticks, setTotalSticks] = useState(0);
  const [note, setNote] = useState("");
  const [rewardNote, setRewardNote] = useState("");
  const [daysLeft, setDaysLeft] = useState("");
  const [lastUpdatedOn, setLastUpdatedOn] = useState("");
  const [progressTitle, setProgressTitle] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState(
    searchParams.get("month") || fallbackMonthLong
  );
  const [selectedYear, setSelectedYear] = useState(
    Number(searchParams.get("year")) || fallbackYear
  );
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [actualTargetVol, setActualTargetVol] = useState<string>("");
  const [overviewAchieved, setOverviewAchieved] = useState(false);
  const [viewMode, setViewMode] = useState<number>(0); // 0 = target+actual, 1 = actual only

  useEffect(() => {
    setSearchParams({ month: selectedMonth, year: selectedYear.toString() });
  }, [selectedMonth, selectedYear, setSearchParams]);

  useEffect(() => {
    if (!contactId) return;

    const fetchData = async () => {
      const token = authContext?.token || localStorage.getItem(KEY_AUTH) || "";
      const monthForApi = (selectedMonth || fallbackMonthLong).slice(0, 3).toLowerCase();
      const yearForApi = String(selectedYear);

      const res = await getSfaData(token, monthForApi, yearForApi, contactId);
      const dataArr = res?.data ?? res;

      if (!dataArr || !Array.isArray(dataArr) || dataArr.length === 0) {
        setProgressTitle("");
        setCurrentSticks(0);
        setTotalSticks(0);
        setNote("");
        setRewardNote("");
        setDaysLeft("");
        setActualTargetVol("0/0");
        return;
      }

      const data = dataArr[0];
      const showActualOnly = Number(data.show_target_only) === 1;
      setViewMode(showActualOnly ? 1 : 0);

      // Parse total from "actual/target" or "actual" string
      const volStr: string = data["total_actual_vol/total_target_vol"] || "0/0";
      if (showActualOnly) {
        const actual = Number(volStr) || 0;
        setCurrentSticks(actual);
        setTotalSticks(0);
        setActualTargetVol(`${actual}`);
      } else {
        const parts = volStr.split("/").map(Number);
        const actual = parts[0] || 0;
        const target = parts[1] || 0;
        setCurrentSticks(actual);
        setTotalSticks(target);
        setActualTargetVol(volStr);
      }

      setNote(data.note || "");
      setRewardNote(data.reward_note || "");
      setDaysLeft(data.days_left_in_target_month || "");
      setProgressTitle(data.progress || "");
      if (data.last_updated_on) {
        const formattedDate = new Date(data.last_updated_on).toLocaleDateString("en-GB",
        {
          day: "numeric",
          month: "short",
          year: "numeric",
        }
      );
      setLastUpdatedOn(formattedDate);
    }
      setOverviewAchieved(Boolean(data.overview_achieved));

      setAvailableMonths(
        (data.month_filter || []).map(
          (m: string) => m.charAt(0).toUpperCase() + m.slice(1)
        )
      );
      setAvailableYears(data.year_filter || []);
    };

    fetchData();
  }, [contactId, selectedMonth, selectedYear, authContext, fallbackMonthLong]);

  const progress = totalSticks > 0 ? Math.min(100, (currentSticks / totalSticks) * 100) : 0;
const formatNote = (text: string) => {
  const parts = text.split(/(₹\d+)/); // split and keep ₹500

  return parts.map((part, index) =>
    /₹\d+/.test(part) ? (
      <strong key={index}>{part}</strong>
    ) : (
      part
    )
  );
};
  return (
    <>
    <Box sx={{background: "linear-gradient(360deg, rgb(249 249 149) 0%, rgb(255 255 243) 100%)",
          borderBottom: "1px solid #e4e4c0;",   width: "100%",  borderRadius: "12px",}}>
      <Box p={2} sx={{ display: "flex", justifyContent: "space-between", mt: 0,  }}>
        <Typography variant="h6" fontWeight="bold">
          Monthly Overview
        </Typography>

        <MonthlyFilter
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          availableMonths={availableMonths}
          availableYears={availableYears}
        />
      </Box>

      <Card
        sx={{
          width: "100%",
          p: 3,
          pt: 0,
          borderRadius: "0px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxShadow: "none",
          background:"none"
         
        }}
      >
        <Box textAlign="center" sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontSize: 14, fontWeight: 500 }}>
            {viewMode === 1 ? `${selectedMonth}'s Target` : progressTitle || "Target progress"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 1,
              gap: 1,
              mb:"10px",
            }}
          >
            {/* <Typography variant="h5" sx={{ fontWeight: 700, fontSize: 24, whiteSpace:"nowrap" }}>
              {viewMode === 1 ? `${currentSticks} Packs` : `${actualTargetVol} <Span> Packs,</Span>`}
            </Typography> */}
           <Typography
  variant="h5"
  sx={{
    fontWeight: 700,
    fontSize: 24,
    whiteSpace: "nowrap",
  }}
>
  {viewMode === 1 ? (
    `${currentSticks} Packs`
  ) : (
    <>
      {actualTargetVol}{" "}
      <span
        style={{
          display: "inline-block",
          position: "relative",
        }}
      >
         Packs
        <Typography
          component="span"
          sx={{
            mt: 0,
            fontSize: "9px",
            position: "absolute",
            right: "-14px",
            width: "100px",
            top: "24px",
            lineHeight: 1.2,
          }}
        >
          (10 Stick = 1 Pack)
        </Typography>
      </span>
    </>
  )}
</Typography>
            
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  background: "#F7F7F7",
                  borderRadius: "999px",
                  padding: "5px 10px",
                  fontWeight: 500,
                  fontSize: 14,
                  color: "#222",
                  height: 25,
                  margin: "auto",
                  width: "120px",
                }}
              >
                <Icons name="timerImage" />
                <span style={{ marginLeft: 6 }}>{daysLeft || "0 days left"}</span>
              </Box>
         
          </Box>
          </Box>
 
          {/* <Typography sx={{ mt: 1, fontSize: 12 }}>{formatNote(note)}</Typography> */}
           {/* Progress Bar */}
        {viewMode === 0 && (
          <Box sx={{ width: "100%", mt: 2, mb:1, }}>
            <Box sx={{ width: "100%", height: 6, borderRadius: 4, background: "#E5E5E5" }}>
              <Box
                sx={{
                  width: `${progress}%`,
                  height: "100%",
                  borderRadius: 4,
                  background: progress >= 100 ? "#28C36C" : "#FBC02D",
                  transition: "width 0.5s, background 0.3s",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 0,
                    transform: "translate(50%, -50%)",
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: progress >= 100 ? "#28C36C" : "#FBC02D",
                    border: "2px solid white",
                    boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
<Typography sx={{ mt: "0px", fontSize: 12 }}> Last updated : {lastUpdatedOn || "-"}</Typography>
          {/* {viewMode === 1 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "#F7F7F7",
                borderRadius: "999px",
                padding: "5px 10px",
                fontWeight: 500,
                fontSize: 14,
                color: "#222",
                height: 25,
                margin: "auto",
                width: "120px",
              }}
            >
              <Icons name="timerImage" />
              <span style={{ marginLeft: 6 }}>{daysLeft || "0 days left"}</span>
            </Box>
          )} */}
        </Box>

        {overviewAchieved && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: "linear-gradient(180deg, #FFE9E2 0%, #FFE9E2 100%)",
              borderRadius: "16px",
              p: 2,
              width: "100%",
              maxWidth: 420,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              position: "relative",
              marginTop: "10px",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "13%",
                right: 0,
                height: "2px",
                background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)",
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                width: "70%",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: "13%",
                right: 0,
                height: "2px",
                background: "linear-gradient(to right, #fff5ee 0%, #ff9800 50%, #fff5ee 100%)",
                borderTopLeftRadius: "16px",
                borderTopRightRadius: "16px",
                width: "70%",
              },
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#FFDED5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "-11px",
                  left: "16px",
                  width: "11px",
                  height: "11px",
                  borderRadius: "50%",
                  backgroundImage: `url(${StarBig})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  opacity: 1,
                  zIndex: 0,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundImage: `url(${StarBig})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  opacity: 1,
                  zIndex: 0,
                },
              }}
            >
              <img src={AlertImg} alt="alert" />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "14px", color: "#222" }}>
                {selectedMonth}'s achievement
              </Typography>
              {rewardNote && (
                <Typography sx={{ fontSize: "12px", color: "#282829", fontWeight: 500 }}>
                  {rewardNote.split(/(\d+\s*Points?)/i).map((part, index) =>
                    /\d+\s*Points?/i.test(part) ? (
                      <Box key={index} component="span" sx={{ fontWeight: 700, color: "#000" }}>
                        {part}
                      </Box>
                    ) : (
                      part
                    )
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        )}

       
      </Card>
      </Box>
    </>
  );
};

export default TargetProgressCard;
